import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Container, Stack, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { ResponseError, type InvitationDetails } from 'api-client';
import { useTeamsApi } from '@/context';
import InvitationCard from './InvitationCard';

export default function InviteAcceptance() {
  const api = useTeamsApi();
  const { invitationId } = useParams<{ invitationId: string }>();
  const generation = useRef<number>(0);
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [pending, setPending] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [retry, setRetry] = useState<number>(0);
  useEffect(() => {
    const current = ++generation.current;
    setInvitation(null);
    setError(null);
    setSuccess(false);
    setPending(false);
    setLoading(true);
    if (!invitationId) {
      setLoading(false);
      setError('Invitation not found.');
      return;
    }
    api
      .invitationAcceptanceRetrieve({ invitationId })
      .then((value) => {
        if (current === generation.current) setInvitation(value);
      })
      .catch((failure: unknown) => {
        if (current === generation.current)
          setError(
            failure instanceof ResponseError && failure.response.status === 404
              ? 'Invitation not found. Ask your team administrator for a new invitation.'
              : 'Could not load this invitation. Please retry.',
          );
      })
      .finally(() => {
        if (current === generation.current) setLoading(false);
      });
    return () => {
      generation.current += 1;
    };
  }, [api, invitationId, retry]);

  const accept = async () => {
    if (!invitationId || !invitation?.canAccept || pending) return;
    const current = generation.current;
    setPending(true);
    setError(null);
    try {
      const result = await api.invitationAcceptanceAccept({ invitationId });
      if (current === generation.current) {
        setInvitation(result);
        setSuccess(true);
      }
    } catch (failure) {
      let message = 'Could not accept this invitation. Please retry.';
      if (failure instanceof ResponseError) {
        const body: unknown = await failure.response.json().catch((): null => null);
        if (body && typeof body === 'object' && typeof Reflect.get(body, 'detail') === 'string')
          message = Reflect.get(body, 'detail');
      }
      if (current !== generation.current) return;
      setError(message);
      try {
        const fresh = await api.invitationAcceptanceRetrieve({ invitationId });
        if (current === generation.current) setInvitation(fresh);
      } catch {
        if (current === generation.current) {
          setInvitation(null);
          setError(message + ' Reload or retry to check its current state.');
        }
      }
    } finally {
      if (current === generation.current) setPending(false);
    }
  };
  return (
    <Container maxWidth="sm">
      <Box py={4}>
        {loading ? (
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress aria-label="Loading invitation" />
            <Typography>Loading invitation…</Typography>
          </Stack>
        ) : invitation ? (
          <InvitationCard
            invitation={invitation}
            pending={pending}
            error={error}
            success={success}
            onAccept={() => {
              void accept();
            }}
          />
        ) : (
          <Stack spacing={2}>
            <Alert severity="error">{error}</Alert>
            <Button onClick={() => setRetry((value) => value + 1)}>Retry Invitation</Button>
          </Stack>
        )}
      </Box>
    </Container>
  );
}
